<template>
  <div class="ktp-camera-page">
    <van-nav-bar
      :title="$t('ktpCamera.title')"
      :border="false"
      left-arrow
      @click-left="onBack"
    />

    <main class="ktp-camera-content">
      <template>
        <section class="intro">
          <div>
            <p>{{ $t('ktpCamera.pageDesc') }}</p>
          </div>
        </section>

        <section v-if="stage === 'camera'" class="capture-section">
          <div class="document-card">
            <div class="document-card__header">
              <div>
                <h1>{{ $t('ktpCamera.frontTitle') }}</h1>
                <p>{{ $t('ktpCamera.frontOnly') }}</p>
              </div>
              <div class="document-card__status is-empty">
                <van-icon name="info-o" />
                <span>{{ $t('ktpCamera.notReady') }}</span>
              </div>
            </div>
            <div class="camera-viewport" :class="{ 'is-camera-open': cameraActive }">
              <video
                v-show="cameraActive"
                ref="video"
                class="camera-video"
                autoplay
                muted
                playsinline
              ></video>
              <div v-if="!cameraActive" class="camera-placeholder">
                <van-icon name="photograph" />
                <span>{{ $t('ktpCamera.emptyPreview') }}</span>
              </div>
              <div v-if="cameraActive" class="guide-mask">
                <div class="ktp-guide">
                  <i class="corner corner--tl"></i>
                  <i class="corner corner--tr"></i>
                  <i class="corner corner--bl"></i>
                  <i class="corner corner--br"></i>
                  <span class="guide-label">KTP</span>
                </div>
              </div>
              <div v-if="cameraActive" class="quality-status" :class="qualityStatusClass">
                <van-icon :name="cameraReady ? 'checked' : 'info-o'" />
                <span>{{ qualityStatusText }}</span>
              </div>
            </div>
            <canvas ref="analysisCanvas" class="analysis-canvas"></canvas>

            <div class="review-actions">
              <button class="secondary-button" type="button" @click="triggerFileInput">
                <van-icon name="photo" />
                {{ $t('ktpCamera.gallery') }}
              </button>
              <button v-if="!cameraActive" class="secondary-button" type="button" @click="openCamera">
                <van-icon name="photograph" />
                {{ $t('ktpCamera.takePhoto') }}
              </button>
              <button v-else class="primary-button" type="button" :disabled="!cameraReady" @click="capturePhoto">
                <van-icon name="photograph" />
                {{ $t('ktpCamera.capture') }}
              </button>
            </div>
          </div>

          <div class="before-send">
            <h2>{{ $t('ktpCamera.beforeSend') }}</h2>
            <div
              v-for="item in checklistItems"
              :key="item.key"
              class="before-send__item"
              :class="{ 'is-warning': item.warning }"
            >
              <van-icon :name="item.warning ? 'info-o' : 'checked'" />
              <span>{{ item.text }}</span>
            </div>
          </div>

          <p class="security-note">{{ $t('ktpCamera.securityNote') }}</p>
        </section>

        <section v-else class="review-section">
          <div class="document-card">
            <div class="document-card__header">
              <div>
                <h1>{{ $t('ktpCamera.frontTitle') }}</h1>
                <p>{{ $t('ktpCamera.frontOnly') }}</p>
              </div>
              <div class="document-card__status" :class="{ 'is-warning': !canConfirm }">
                <van-icon :name="canConfirm ? 'checked' : 'info-o'" />
                <span>{{ canConfirm ? $t('ktpCamera.readyShort') : $t('ktpCamera.checkAgain') }}</span>
              </div>
            </div>
            <div class="review-image-wrap">
              <img :src="processed.url" :alt="$t('ktpCamera.previewAlt')">
            </div>
            <div class="review-actions">
              <button class="secondary-button" type="button" @click="triggerFileInput">
                <van-icon name="photo" />
                {{ $t('ktpCamera.gallery') }}
              </button>
              <button class="secondary-button" type="button" @click="retakePhoto">
                <van-icon name="photograph" />
                {{ $t('ktpCamera.retakePhoto') }}
              </button>
            </div>
          </div>

          <div class="before-send">
            <h2>{{ $t('ktpCamera.beforeSend') }}</h2>
            <div
              v-for="item in checklistItems"
              :key="item.key"
              class="before-send__item"
              :class="{ 'is-warning': item.warning }"
            >
              <van-icon :name="item.warning ? 'info-o' : 'checked'" />
              <span>{{ item.text }}</span>
            </div>
          </div>

          <p class="security-note">{{ $t('ktpCamera.securityNote') }}</p>

          <div class="submit-bar">
            <button class="primary-button" type="button" :disabled="submitting || !canConfirm" @click="confirmImage">
              {{ submitting ? $t('loading') : $t('ktpCamera.sendVerification') }}
            </button>
          </div>

          <div v-if="!canConfirm" class="review-result">
            <div class="review-result__title">
              <van-icon name="info-o" />
              {{ $t('ktpCamera.qualityWarning') }}
            </div>
            <p>{{ qualityDescription }}</p>
          </div>
        </section>
        <input ref="fileInput" class="file-input" type="file" accept="image/*" @change="onFileChange">
      </template>
    </main>
  </div>
</template>

<script>
import { Icon, NavBar } from 'vant'
import routerBack from '@/mixins/routerBack'
import {
  getKycDocumentUploadUrl,
  getKycSession,
  submitKycDocument
} from '@/api/kyc'
import {
  readKycV2Context,
  uploadKycDocumentToOss
} from '@/utils/kycV2'
import { handleKycV2View } from '@/utils/kycV2Flow'
import { processKtpImage } from '@/utils/ktpImage'
import { inspectKtpCanvas } from '@/utils/ktpVision'

export default {
  name: 'KtpCamera',
  mixins: [routerBack],
  components: {
    'van-icon': Icon,
    'van-nav-bar': NavBar
  },
  data() {
    return {
      stage: 'camera',
      cameraActive: false,
      cameraStream: null,
      cameraCheckTimer: null,
      cameraReady: false,
      cardDetected: false,
      detectionReason: '',
      passStreak: 0,
      failStreak: 0,
      noCardStreak: 0,
      inspectionInProgress: false,
      qualityStatus: 'idle',
      processed: null,
      submitting: false
    }
  },
  computed: {
    qualityStatusClass() {
      return `quality-status--${this.qualityStatus}`
    },
    qualityStatusText() {
      if (this.qualityStatus === 'ready') return this.$t('ktpCamera.ready')
      if (this.detectionReason === 'out-of-frame') return this.$t('ktpCamera.keepInsideFrame')
      if (this.detectionReason === 'too-small') return this.$t('ktpCamera.moveCloser')
      if (this.qualityStatus === 'checking' && this.cardDetected) return this.$t('ktpCamera.analyzing')
      return this.$t('ktpCamera.align')
    },
    qualityDescription() {
      if (!this.processed) return ''
      if (this.processed.quality.passed) return this.$t('ktpCamera.qualityPassedDesc')
      const reason = this.processed.quality.reason
      if (reason === 'no-card') return this.$t('ktpCamera.notKtpPhoto')
      if (reason === 'out-of-frame') return this.$t('ktpCamera.cornersNotVisible')
      if (reason === 'too-small') return this.$t('ktpCamera.ktpTooSmall')
      if (reason === 'too-dark') return this.$t('ktpCamera.tooDark')
      if (reason === 'too-bright') return this.$t('ktpCamera.tooBright')
      return this.$t('ktpCamera.lowQuality')
    },
    canConfirm() {
      return !!(this.processed && this.processed.quality && this.processed.quality.passed)
    },
    checklistItems() {
      const reason = this.processed && this.processed.quality && this.processed.quality.reason
      const hasPhoto = !!this.processed
      return [
        {
          key: 'textPortrait',
          text: reason === 'no-card' ? this.$t('ktpCamera.checkUseKtpFront') : this.$t('ktpCamera.checkTextPortrait'),
          warning: hasPhoto && ['no-card', 'too-small', 'low-quality'].includes(reason)
        },
        {
          key: 'glare',
          text: this.$t('ktpCamera.checkNoGlare'),
          warning: hasPhoto && ['too-bright', 'low-quality'].includes(reason)
        },
        {
          key: 'corners',
          text: reason === 'out-of-frame' ? this.$t('ktpCamera.checkCornersVisible') : this.$t('ktpCamera.checkCorners'),
          warning: hasPhoto && ['no-card', 'out-of-frame', 'too-small'].includes(reason)
        }
      ]
    }
  },
  beforeDestroy() {
    this.stopCamera()
    this.revokeUrl(this.processed)
  },
  methods: {
    onBack() {
      this.stopCamera()
      this.goBack()
    },
    async openCamera() {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        this.$toast(this.$t('ktpCamera.cameraUnsupported'))
        return
      }
      try {
        this.stopCamera()
        this.cameraStream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: {
            facingMode: { ideal: 'environment' },
            width: { ideal: 1920 },
            height: { ideal: 1080 }
          }
        })
        this.cameraActive = true
        this.qualityStatus = 'checking'
        this.cardDetected = false
        this.detectionReason = ''
        this.passStreak = 0
        this.failStreak = 0
        this.noCardStreak = 0
        await this.$nextTick()
        const video = this.$refs.video
        video.srcObject = this.cameraStream
        await video.play()
        this.startCameraCheck()
      } catch (error) {
        this.stopCamera()
        this.$toast(this.$t('ktpCamera.cameraFailed'))
      }
    },
    startCameraCheck() {
      this.clearCameraCheck()
      this.cameraCheckTimer = window.setInterval(() => {
        this.inspectVideoFrame()
      }, 250)
    },
    clearCameraCheck() {
      if (this.cameraCheckTimer) {
        window.clearInterval(this.cameraCheckTimer)
        this.cameraCheckTimer = null
      }
    },
    async inspectVideoFrame() {
      const video = this.$refs.video
      const canvas = this.$refs.analysisCanvas
      if (!video || !canvas || !video.videoWidth || this.inspectionInProgress) return
      this.inspectionInProgress = true
      try {
      const width = 320
      const viewport = this.$el.querySelector('.camera-viewport')
      const viewportRatio = viewport && viewport.clientWidth && viewport.clientHeight
        ? viewport.clientWidth / viewport.clientHeight
        : 1.584
      // 必须和 object-fit: cover 后用户看到的摄像头区域保持同一比例，
      // 否则检测区域会与绿色取景框错位。
      const height = Math.max(180, Math.round(width / viewportRatio))
      canvas.width = width
      canvas.height = height
      const context = canvas.getContext('2d')
      const sourceRatio = video.videoWidth / video.videoHeight
      const targetRatio = viewportRatio
      let sx = 0
      let sy = 0
      let sw = video.videoWidth
      let sh = video.videoHeight
      if (sourceRatio > targetRatio) {
        sw = Math.round(video.videoHeight * targetRatio)
        sx = Math.round((video.videoWidth - sw) / 2)
      } else {
        sh = Math.round(video.videoWidth / targetRatio)
        sy = Math.round((video.videoHeight - sh) / 2)
      }
      context.drawImage(video, sx, sy, sw, sh, 0, 0, width, height)
      const result = await inspectKtpCanvas(canvas)
      this.detectionReason = result.reason
      if (!result.cardDetected) {
        this.noCardStreak += 1
        this.passStreak = 0
        this.failStreak = 0
      } else if (result.passed) {
        this.noCardStreak = 0
        this.passStreak += 1
        this.failStreak = 0
      } else {
        this.noCardStreak = 0
        this.failStreak += 1
        this.passStreak = 0
      }

      // 需要连续两帧通过才启用拍照，避免单帧误判。
      if (this.passStreak >= 2) this.cameraReady = true
      // 身份证离开或明显变小时快速取消，清晰度短暂波动则保留少量容错。
      if (this.noCardStreak >= 2 || this.failStreak >= 2) this.cameraReady = false
      this.cardDetected = result.cardDetected
      this.qualityStatus = this.cameraReady ? 'ready' : 'checking'
      } finally {
        this.inspectionInProgress = false
      }
    },
    capturePhoto() {
      const video = this.$refs.video
      if (!video || !video.videoWidth) {
        this.$toast(this.$t('ktpCamera.cameraFailed'))
        return
      }
      if (!this.cameraReady) {
        this.$toast(this.$t('ktpCamera.align'))
        return
      }
      const canvas = document.createElement('canvas')
      canvas.width = video.videoWidth
      canvas.height = video.videoHeight
      canvas.getContext('2d').drawImage(video, 0, 0)
      canvas.toBlob(blob => {
        if (!blob) {
          this.$toast(this.$t('ktpCamera.processFailed'))
          return
        }
        const file = new File([blob], `ktp-camera-${Date.now()}.jpg`, { type: 'image/jpeg' })
        this.processFile(file)
      }, 'image/jpeg', 0.92)
    },
    triggerFileInput() {
      this.$refs.fileInput.click()
    },
    onFileChange(event) {
      const file = event.target.files && event.target.files[0]
      event.target.value = ''
      if (file) this.processFile(file)
    },
    async processFile(file) {
      this.clearCameraCheck()
      try {
        this.stopCamera()
        const result = await processKtpImage(file, { side: 'front' })
        const completeness = await this.inspectProcessedImage(result.blob)
        result.quality = {
          ...result.quality,
          ...completeness,
          passed: result.quality.passed && completeness.passed,
          reason: completeness.reason || result.quality.reason
        }
        this.revokeUrl(this.processed)
        this.processed = {
          ...result,
          url: URL.createObjectURL(result.blob)
        }
        this.stage = 'review'
      } catch (error) {
        this.$toast(this.$t('ktpCamera.processFailed'))
      }
    },
    inspectProcessedImage(blob) {
      return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(blob)
        const image = new Image()
        image.onload = async () => {
          try {
            const width = 320
            const height = Math.max(180, Math.round(width / (image.naturalWidth / image.naturalHeight || 1.584)))
            const canvas = document.createElement('canvas')
            canvas.width = width
            canvas.height = height
            canvas.getContext('2d').drawImage(image, 0, 0, width, height)
            const result = await inspectKtpCanvas(canvas)
            resolve(result)
          } catch (e) {
            reject(e)
          } finally {
            URL.revokeObjectURL(url)
          }
        }
        image.onerror = () => {
          URL.revokeObjectURL(url)
          reject(new Error('Unable to inspect image'))
        }
        image.src = url
      })
    },
    retake() {
      this.revokeUrl(this.processed)
      this.processed = null
      this.cameraReady = false
      this.cardDetected = false
      this.detectionReason = ''
      this.passStreak = 0
      this.failStreak = 0
      this.noCardStreak = 0
      this.qualityStatus = 'idle'
      this.stage = 'camera'
    },
    async retakePhoto() {
      this.retake()
      await this.$nextTick()
      this.openCamera()
    },
    async confirmImage() {
      if (!this.processed) return
      if (!this.canConfirm) {
        this.$toast(this.$t('ktpCamera.lowQuality'))
        return
      }
      const context = readKycV2Context()
      const sessionId = this.$route.query.sessionId || context.sessionId
      if (!sessionId) {
        this.$toast(this.$t('ktpCamera.sessionExpired'))
        return
      }
      this.submitting = true
      try {
        const uploadRes = await getKycDocumentUploadUrl(sessionId, {
          fileName: this.processed.file.name
        })
        if (uploadRes.code !== 'Success') {
          if (await this.continueLatestKycStep(sessionId, uploadRes)) return
          this.$toast(this.getKycSubmitErrorMessage(uploadRes))
          return
        }
        if (!this.isValidUploadInfo(uploadRes.data)) {
          this.$toast(this.$t('ktpCamera.documentServiceUnavailable'))
          return
        }
        await uploadKycDocumentToOss(uploadRes.data, this.processed.file)
        const submitRes = await submitKycDocument(sessionId, {
          ossKey: uploadRes.data.ossKey || uploadRes.data.key
        })
        if (submitRes.code !== 'Success') {
          if (await this.continueLatestKycStep(sessionId, submitRes)) return
          this.$toast(this.getKycSubmitErrorMessage(submitRes))
          return
        }
        const view = submitRes.data || {}
        await this.handleLatestKycView(view, sessionId)
      } catch (error) {
        console.error('Submit KYC document failed:', error)
        this.$toast(this.getKycSubmitErrorMessage(error))
      } finally {
        this.submitting = false
      }
    },
    isValidUploadInfo(uploadInfo = {}) {
      return !!(
        uploadInfo.host &&
        (uploadInfo.ossKey || uploadInfo.key) &&
        uploadInfo.policy &&
        uploadInfo.OSSAccessKeyId &&
        uploadInfo.signature &&
        uploadInfo.contentType
      )
    },
    isKycStepChanged(error = {}) {
      const responseData = error && error.response && error.response.data
      const source = responseData || error || {}
      return (source.code || source.errorCode) === 'Err.KycSessionInvalidState'
    },
    async continueLatestKycStep(sessionId, error) {
      if (!this.isKycStepChanged(error)) return false
      const res = await getKycSession(sessionId)
      if (res.code !== 'Success') return false
      await this.handleLatestKycView(res.data || {}, sessionId)
      return true
    },
    async handleLatestKycView(view = {}, sessionId) {
      const context = readKycV2Context()
      const ready = handleKycV2View(this, view, {
        ...context,
        sessionId: view.sessionId || sessionId
      })
      if (ready) {
        this.$router.replace({
          path: context.returnPath || '/index',
          query: { kycV2Return: '1' }
        })
        return
      }
      if (view.nextAction === 'UPLOAD_DOCUMENT') this.retake()
    },
    getKycSubmitErrorMessage(error, fallback) {
      const responseData = error && error.response && error.response.data
      const isRuntimeError = error instanceof Error
      const source = responseData || (isRuntimeError ? {} : error || {})
      const code = source.code || source.errorCode
      const message = source.errorMessage || source.message || source.msg
      const systemMessage = this.$t('systemError')

      const codeMessages = {
        'Err.KycInvalidDocument': this.$t('ktpCamera.invalidDocument'),
        'Err.KycDocumentVerificationFailed': this.$t('ktpCamera.invalidDocument'),
        'Err.KycDocumentVerificationTemporaryUnavailable': this.$t('ktpCamera.documentServiceUnavailable'),
        'Err.KycSessionInvalidState': this.$t('ktpCamera.sessionExpired'),
        'Err.KycSessionNotFound': this.$t('ktpCamera.sessionExpired'),
        'Err.KycSessionExpired': this.$t('ktpCamera.sessionExpired')
      }
      if (codeMessages[code]) return codeMessages[code]
      if (message && message !== systemMessage) return message

      if (error && error.message) {
        return this.$t('ktpCamera.uploadNetworkFailed')
      }
      return fallback || message || systemMessage
    },
    revokeUrl(item) {
      if (item && item.url) URL.revokeObjectURL(item.url)
    },
    stopCamera() {
      this.clearCameraCheck()
      if (this.cameraStream) {
        this.cameraStream.getTracks().forEach(track => track.stop())
        this.cameraStream = null
      }
      this.cameraActive = false
    },
    formatSize(size) {
      if (!size) return '0 KB'
      return `${(size / 1024).toFixed(size > 1024 * 1024 ? 1 : 0)} ${size > 1024 * 1024 ? 'MB' : 'KB'}`
    }
  }
}
</script>

<style lang="less" scoped>
.ktp-camera-page {
  min-height: 100vh;
  background: #f5f6fa;
  color: #2f3340;

  ::v-deep .van-nav-bar {
    background: #fff;
  }

  ::v-deep .van-nav-bar__title {
    font-size: 32px;
    font-weight: 500;
    color: #2f3340;
  }
}

.ktp-camera-content {
  min-height: calc(100vh - 92px);
  padding: 26px 28px 172px;
  box-sizing: border-box;
}

.intro {
  margin-bottom: 18px;

  p {
    margin: 0;
    color: #5d6372;
    font-size: 26px;
    line-height: 38px;
  }
}

.capture-section,
.review-section {
  padding: 0;
  border-radius: 0;
  background: transparent;
  box-shadow: none;
}

.document-card,
.before-send {
  padding: 24px;
  border-radius: 12px;
  background: #fff;
  border: 1px solid #eceff5;
}

.document-card {
  &__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 22px;

    h1 {
      margin: 0;
      color: #303440;
      font-size: 30px;
      line-height: 38px;
      font-weight: 500;
    }

    p {
      margin: 4px 0 0;
      color: #686f7d;
      font-size: 24px;
      line-height: 32px;
    }
  }

  &__status {
    display: flex;
    align-items: center;
    flex-shrink: 0;
    margin-top: 4px;
    color: #55b979;
    font-size: 24px;
    line-height: 32px;

    .van-icon {
      margin-right: 6px;
      font-size: 26px;
    }

    &.is-warning {
      color: #d6902e;
    }

    &.is-empty {
      color: #99a0ad;
    }
  }
}

.camera-viewport {
  position: relative;
  height: auto;
  min-height: 0;
  max-height: none;
  aspect-ratio: 1.586;
  overflow: hidden;
  border-radius: 0;
  background: #f1f2f5;

  &.is-camera-open {
    background: #101a2f;
  }
}

.camera-video {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.camera-placeholder {
  position: absolute;
  top: 50%;
  left: 50%;
  z-index: 1;
  display: flex;
  transform: translate(-50%, -50%);
  flex-direction: column;
  align-items: center;
  width: 80%;
  color: #9aa2b2;
  text-align: center;

  .van-icon {
    margin-bottom: 12px;
    font-size: 64px;
  }

  span {
    font-size: 24px;
    line-height: 34px;
  }
}

.guide-mask {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.ktp-guide {
  position: relative;
  width: 92%;
  max-width: 720px;
  aspect-ratio: 1.586;
  border: 2px solid rgba(255, 255, 255, .44);
  border-radius: 10px;
  box-shadow: 0 0 0 9999px rgba(4, 12, 31, .54);
}

.corner {
  position: absolute;
  width: 34px;
  height: 34px;
  border-color: #45d39b;
  border-style: solid;

  &--tl { top: -2px; left: -2px; border-width: 5px 0 0 5px; border-radius: 8px 0 0 0; }
  &--tr { top: -2px; right: -2px; border-width: 5px 5px 0 0; border-radius: 0 8px 0 0; }
  &--bl { bottom: -2px; left: -2px; border-width: 0 0 5px 5px; border-radius: 0 0 0 8px; }
  &--br { right: -2px; bottom: -2px; border-width: 0 5px 5px 0; border-radius: 0 0 8px 0; }
}

.guide-label {
  position: absolute;
  top: 50%;
  left: 50%;
  color: rgba(255, 255, 255, .55);
  font-size: 30px;
  letter-spacing: 4px;
  transform: translate(-50%, -50%);
}

.quality-status {
  position: absolute;
  bottom: 18px;
  left: 50%;
  z-index: 2;
  display: flex;
  align-items: center;
  padding: 10px 18px;
  border-radius: 24px;
  background: rgba(8, 18, 39, .72);
  color: #fff;
  font-size: 22px;
  line-height: 30px;
  transform: translateX(-50%);
  white-space: nowrap;

  .van-icon { margin-right: 8px; font-size: 24px; }
  &--ready { background: rgba(17, 126, 81, .82); }
}

.analysis-canvas,
.file-input {
  display: none;
}

.capture-actions,
.review-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
}

button {
  font-family: inherit;
  cursor: pointer;
}

.primary-button,
.secondary-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 72px;
  padding: 0 24px;
  border-radius: 10px;
  font-size: 26px;
  line-height: 34px;
  font-weight: 500;
  transition: opacity .2s ease;

  .van-icon { margin-right: 10px; font-size: 30px; }
  &:active { opacity: .78; }
}

.primary-button {
  flex: 1;
  border: 0;
  background: #143cf5;
  color: #fff;

  &:disabled {
    opacity: .45;
  }
}

.secondary-button {
  flex: 1;
  border: 2px solid #2d3e9b;
  background: #fff;
  color: #2d3e9b;
}

.review-image-wrap {
  overflow: hidden;
  border-radius: 0;
  background: #f1f2f5;

  img {
    display: block;
    width: 100%;
    aspect-ratio: 1.586;
    object-fit: cover;
  }
}

.review-result {
  margin-top: 18px;
  padding: 20px 22px;
  border-radius: 12px;
  background: #fff7ed;

  &__title {
    color: #d6902e;
    font-size: 24px;
    line-height: 32px;
    font-weight: 600;
    .van-icon { margin-right: 8px; }
  }

  p {
    margin: 8px 0 0;
    color: #7b8498;
    font-size: 23px;
    line-height: 34px;
  }

  &__meta {
    display: flex;
    flex-wrap: wrap;
    gap: 8px 20px;
    margin-top: 12px;
    color: #52617a;
    font-size: 21px;
    line-height: 30px;
  }
}

.review-actions {
  margin-top: 22px;
}

.before-send {
  margin-top: 20px;

  h2 {
    margin: 0 0 16px;
    color: #303440;
    font-size: 30px;
    line-height: 38px;
    font-weight: 500;
  }

    &__item {
    display: flex;
    align-items: center;
    color: #69707e;
    font-size: 25px;
    line-height: 36px;

    & + & {
      margin-top: 10px;
    }

    .van-icon {
      flex-shrink: 0;
      margin-right: 12px;
      color: #4cbd73;
      font-size: 28px;
    }

    &.is-warning {
      color: #9a5b00;

      .van-icon {
        color: #f59f00;
      }
    }
  }
}

.security-note {
  margin: 18px 0 0;
  color: #6e7480;
  font-size: 24px;
  line-height: 34px;
  text-align: center;
}

.submit-bar {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10;
  padding: 20px 28px calc(20px + env(safe-area-inset-bottom));
  background: #f5f6fa;
  box-sizing: border-box;

  .primary-button {
    width: 100%;
    min-height: 78px;
    font-size: 28px;
  }
}

@media (max-width: 375px) {
  .ktp-camera-content { padding: 20px 18px 40px; }
  .intro { display: block; }
  .capture-section, .review-section { padding: 0; }
  .document-card, .before-send { padding: 18px; }
  .capture-actions { flex-wrap: wrap; }
  .capture-actions .primary-button, .capture-actions .secondary-button { min-width: calc(50% - 12px); }
}
</style>
